import { createApi } from 'ReduxHelpers';
import { getBackRoyalApiBaseConfig } from 'BackRoyalApi';
import {
    type AdminProjectGroup,
    type AdminProjectGroupMembership,
    type CreateProjectGroupParams,
    type InviteMemberParams,
    type MembershipsResponse,
    type MembershipUpdateResult,
    type ProjectGroupCreateResponse,
    type ProjectGroupFilters,
    type ProjectGroupsIndexResponse,
    type UpdateMembershipStatusParams,
} from './AdminCohortProjectGroups.types';

/*
    The admin endpoints for project groups. They are separate from the student-facing
    ProjectGroup/projectGroupsApi because they return ADMIN_FIELDS payloads, which carry the
    memberships and the member emails that the student payloads leave out.

    The base query snake-cases request bodies and params and camel-cases responses. It also
    JSON-encodes a nested params object, so `filters` goes over the wire as one string and
    `decode_json_params` in Api::ApiCrudControllerBase turns it back into a hash.
*/

// The shared base query retries a disconnect and a 502/504, and Rails may already have committed
// the write, so a retry would create a second group or a second membership.
const NO_RETRY = { maxRetries: 0 };

function firstProjectGroup(response: ProjectGroupCreateResponse): AdminProjectGroup {
    const record = response.contents.projectGroups?.[0];
    if (!record) throw new Error('Unexpected response shape from the project group create');
    return record;
}

function firstMembership(response: MembershipsResponse): AdminProjectGroupMembership {
    const record = response.contents.projectGroupMemberships?.[0];
    if (!record) throw new Error('Unexpected response shape from the project group membership create');
    return record;
}

export const adminProjectGroupsApi = createApi('adminProjectGroupsApi', {
    ...getBackRoyalApiBaseConfig('adminProjectGroupsApi'),
    endpoints: builder => ({
        getProjectGroups: builder.query<AdminProjectGroup[], ProjectGroupFilters>({
            query: filters => ({
                url: 'project_groups.json',
                params: { filters },
            }),
            transformResponse: (response: ProjectGroupsIndexResponse) => response.contents.projectGroups || [],
        }),

        // Creates the group and makes the named user its creator.
        createProjectGroup: builder.mutation<AdminProjectGroup, CreateProjectGroupParams>({
            query: ({ cohortId, requirementIdentifier, creatorEmail }) => ({
                method: 'POST',
                url: 'project_groups.json',
                body: { record: { cohortId, requirementIdentifier, memberEmails: [], creatorEmail } },
            }),
            transformResponse: firstProjectGroup,
            extraOptions: NO_RETRY,
        }),

        inviteMember: builder.mutation<AdminProjectGroupMembership, InviteMemberParams>({
            query: params => ({
                method: 'POST',
                url: 'project_group_memberships.json',
                body: params,
            }),
            transformResponse: firstMembership,
            extraOptions: NO_RETRY,
        }),

        updateMembershipStatus: builder.mutation<MembershipUpdateResult, UpdateMembershipStatusParams>({
            query: ({ membershipId, endpoint }) => ({
                method: 'PUT',
                url: `project_group_memberships/${membershipId}/${endpoint}.json`,

                // An admin removal skips the check that stops a member from leaving a group that
                // already submitted its project.
                ...(endpoint === 'remove' && { body: { meta: { removalForced: true } } }),
            }),
            transformResponse: (response: MembershipsResponse) => ({
                memberships: response.contents.projectGroupMemberships || [],
                newCreatorMembership: response.meta?.newCreatorMembership || null,
            }),
            extraOptions: NO_RETRY,
        }),
    }),
});
